'use client';

import Image from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Align,
    Button,
    ButtonList,
    ButtonVariant,
    Checkbox,
    Column,
    Columns,
    Divider,
    Icon,
    IconName,
    Justify,
    Link,
    ModalBody,
    ModalFooter,
    Spacer,
    SpacerSize,
    StatusState,
    Text,
    TextMarkup,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { TradeInQuoteType } from '@app-types/trade-in';

import { Loader } from '@components/loader';

import { TradeInStep } from '@constants/trade-in';

import recommerceIcon from '@assets/logo-recommerce.svg';
import {
    clearTradeInProduct,
    getTradeInStep,
    sendTradeInConfirmationTracking,
    setTradeInStep,
} from '@product-detail/helpers';

import { TradeInBillLine, TradeInBillLineMobile } from './components';

export type MobileTradeInStep3Props = {
    step: TradeInStep;
    setStep: (value: TradeInStep) => void;
    quote: TradeInQuoteType | undefined;
    tradeInBonus: number;
    closeModal: () => void;
    isLoading: boolean;
    isRenewal: boolean;
};

export function MobileTradeInStep3({
    step,
    setStep,
    quote,
    tradeInBonus,
    closeModal,
    isLoading,
    isRenewal,
}: Readonly<MobileTradeInStep3Props>) {
    const [phoneName, setPhoneName] = useState<string>('');
    const [hasAccepted, setHasAccepted] = useState<boolean>(false);

    const hasBonus = useMemo(() => tradeInBonus > 0, [tradeInBonus]);

    const totalLabel = useMemo(() => (hasBonus ? 'Total' : 'Valeur estimée de votre téléphone'), [hasBonus]);
    const totalIcon = useMemo(() => (hasBonus ? IconName.EQUAL : undefined), [hasBonus]);

    const handleConfirmation = () => {
        closeModal();
        if (quote && quote.estimatedRecoveryAmount >= 10) {
            const product = getTradeInStep(TradeInStep.SEARCH);
            sendTradeInConfirmationTracking(isRenewal, product?.name ?? '', quote.estimatedRecoveryAmount);
            setTradeInStep(TradeInStep.CONFIRMED, undefined);
            setStep(TradeInStep.CONFIRMED);
        } else {
            clearTradeInProduct();
            setStep(TradeInStep.SEARCH);
        }
    };

    const handleReturn = () => setStep(TradeInStep.FORM);

    useEffect(() => {
        const product = getTradeInStep(TradeInStep.SEARCH);

        if (phoneName !== product?.name) {
            setPhoneName(product?.name ?? '');
        }
    }, [step]);

    if (step !== TradeInStep.CONFIRMATION) return null;

    return (
        <>
            <ModalBody>
                {isLoading && (
                    <View flexable justify={Justify.CENTER}>
                        <Loader />
                    </View>
                )}
                {!isLoading && quote && quote.estimatedRecoveryAmount >= 10 ? (
                    <>
                        <Link role='button' onClick={handleReturn} className='is-flex is-aligned-center'>
                            <Icon name={IconName.ARROW_LEFT} />
                            &nbsp;Retour
                        </Link>
                        <Text>
                            <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Finalisez la reprise de votre {phoneName}&nbsp;
                            </Text>
                            <Text markup={TextMarkup.SPAN}>(estimation valide jusqu&apos;à 23h59 ce soir).</Text>
                        </Text>
                        <div className='is-hidden-mobile'>
                            <Columns verticalAlign={Align.CENTER} mobile marginless>
                                {hasBonus && (
                                    <>
                                        <Column size={4}>
                                            <TradeInBillLine
                                                amount={quote.estimatedRecoveryAmount}
                                                label='Valeur estimée de votre téléphone'
                                            />
                                        </Column>
                                        <Column size={4}>
                                            <TradeInBillLine
                                                amount={tradeInBonus}
                                                label='Bonus reprise'
                                                icon={IconName.PLUS_CIRCLE}
                                            />
                                        </Column>
                                    </>
                                )}
                                <Column size={4}>
                                    <TradeInBillLine
                                        amount={quote.estimatedRecoveryAmount + tradeInBonus}
                                        label={totalLabel}
                                        isOverlined
                                        icon={totalIcon}
                                    />
                                </Column>
                            </Columns>
                        </div>
                        <div className='is-hidden-tablet'>
                            {hasBonus && (
                                <>
                                    <TradeInBillLineMobile
                                        amount={quote.estimatedRecoveryAmount}
                                        label='Valeur estimée de votre téléphone'
                                        isFullwidth={false}
                                    />

                                    <TradeInBillLineMobile
                                        amount={tradeInBonus}
                                        label='Bonus reprise'
                                        icon={IconName.PLUS_CIRCLE}
                                        isFullwidth={false}
                                    />
                                </>
                            )}
                            <TradeInBillLineMobile
                                amount={quote.estimatedRecoveryAmount + tradeInBonus}
                                label={totalLabel}
                                isOverlined
                                icon={totalIcon}
                                isFullwidth={!hasBonus}
                            />
                        </div>
                        <Divider />
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Utilisez l&apos;enveloppe préaffranchie, on garantit votre envoi&nbsp;!
                        </Text>
                        <Text>
                            <Text markup={TextMarkup.SPAN}>
                                Vous recevrez une enveloppe préaffranchie à votre adresse de facturation.&nbsp;
                            </Text>
                            <Image src={recommerceIcon} alt='recommerce' width={100} />
                            <Text markup={TextMarkup.SPAN}>
                                &nbsp;garantit le transport de votre téléphone et prend en charge les coûts
                                d&apos;affranchissement.
                            </Text>
                        </Text>
                        <Divider />
                        <View flexable className='is-justified-start-mobile is-justified-end-tablet'>
                            <Checkbox checked={hasAccepted} onChange={() => setHasAccepted(!hasAccepted)}>
                                <Text>
                                    <Text markup={TextMarkup.SPAN}>J&apos;accepte les&nbsp;</Text>
                                    <Text markup={TextMarkup.SPAN}>
                                        <Link
                                            href='https://assets.recommerce.com/buyback-webgen-app/bouyguestelecom/fr/CGA.Bouygues-Recommerce.pdf'
                                            blank
                                        >
                                            Conditions Générales de Reprise
                                        </Link>
                                    </Text>
                                </Text>
                            </Checkbox>
                        </View>
                    </>
                ) : (
                    <>
                        <Link role='button' onClick={handleReturn} className='is-flex is-aligned-center'>
                            <Icon name={IconName.ARROW_LEFT} />
                            &nbsp;Retour
                        </Link>
                        <Alert
                            status={StatusState.INFO}
                            title='Malheureusement, nous ne pouvons pas reprendre votre mobile en ligne car sa valeur estimée est inférieure à 10€.'
                        />
                        <Spacer size={SpacerSize.TWO} />
                        <Text>Cependant, nous pouvons le recycler gratuitement en boutique.</Text>
                        <Text>
                            <Text markup={TextMarkup.SPAN}>Vous pouvez essayer de&nbsp;</Text>
                            <Text markup={TextMarkup.SPAN}>
                                <Link onClick={() => setStep(TradeInStep.SEARCH)}>
                                    faire estimer un autre mobile&nbsp;
                                </Link>
                            </Text>
                            <Text markup={TextMarkup.SPAN}>
                                pour bénéficier du bonus reprise ou finaliser votre achat.
                            </Text>
                        </Text>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
                        Fermer
                    </Button>
                    <Button
                        variant={ButtonVariant.CONVERSION}
                        disabled={!hasAccepted && quote && quote.estimatedRecoveryAmount >= 10}
                        onClick={handleConfirmation}
                        loading={isLoading}
                    >
                        {quote && quote.estimatedRecoveryAmount >= 10
                            ? 'Finaliser la reprise'
                            : 'Finaliser votre achat'}
                    </Button>
                </ButtonList>
            </ModalFooter>
        </>
    );
}
